import {
	CheckCircleFilled,
	CloseCircleOutlined,
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	FileExcelOutlined,
	FilterOutlined,
	LockOutlined,
	MoreOutlined,
	ReloadOutlined,
	SwapOutlined,
	UnlockOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Button, Card, DatePicker, Dropdown, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CopyableText from '../../components/copyable-text';
import DeviceDetails from '../../components/device-details';
import SwapDeviceModal from '../../components/swap-device-modal';
import axios from '../../config/axios';
import { AuthContext } from '../../context/auth.context';
import AppLayout from '../../layouts/app.layout';
import { Device } from '../../types/device.types';
import { RetailerMinimal } from '../../types/retailers.types';
import { UserMinimal } from '../../types/user.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;

interface FilterValues {
	status: string | null;
	device_id: number | null;
	phone_number: string | null;
	imei: string | null;
	retailer: string | null;
	sold_by: string | null;
	user: number | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const Devices = () => {
	const { user } = useContext(AuthContext);
	const queryClient = useQueryClient();
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	// /admin/devices?device=<id> opens that contract.
	const [searchParams] = useSearchParams();
	const linkedDeviceId = Number(searchParams.get('device')) || null;
	const [filters, setFilters] = useState<FilterValues>({
		status: null,
		device_id: linkedDeviceId,
		phone_number: null,
		imei: null,
		retailer: null,
		sold_by: null,
		user: null,
		date_range: null,
	});
	const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
	const [commentModalVisible, setCommentModalVisible] = useState(false);
	const [currentDeviceId, setCurrentDeviceId] = useState<number | null>(null);
	const [commentValue, setCommentValue] = useState<string>('');
	const [commentLoading, setCommentLoading] = useState(false);
	const [soldByModalVisible, setSoldByModalVisible] = useState(false);
	const [vendorIdValue, setVendorIdValue] = useState<string>('');
	const [soldByLoading, setSoldByLoading] = useState(false);
	const [swapDeviceModalVisible, setSwapDeviceModalVisible] = useState(false);
	const [swapOldDeviceId, setSwapOldDeviceId] = useState<number>(0);
	const [swapNewDeviceId, setSwapNewDeviceId] = useState<number | null>(null);
	const [writeOffDevice, setWriteOffDevice] = useState<Device | null>(null);
	const [writeOffReason, setWriteOffReason] = useState<string>('');
	const [writeOffBusy, setWriteOffBusy] = useState(false);

	const { data, error, isLoading } = useQuery({
		queryKey: ['devices', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				status: filters.status,
				device_id: filters.device_id,
				phone_number: filters.phone_number,
				imei: filters.imei,
				retailer: filters.retailer,
				sold_by: filters.sold_by,
				user: filters.user,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/admin/devices', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const devices: Device[] = data?.results || [];

	useEffect(() => {
		if (!linkedDeviceId) return;
		const linked = devices.find((d) => d.id === linkedDeviceId);
		if (linked) setSelectedDevice(linked);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [linkedDeviceId, data]);

	const getStatusColor = (status: Device['status']) => {
		switch (status) {
			case 'pending':
				return 'green';
			case 'approved':
				return 'blue';
			case 'rejected':
				return 'red';
			default:
				return 'default';
		}
	};

	const handleDeleteDevice = async (deviceId: number) => {
		try {
			await axios.delete(`/admin/super/devices/${deviceId}`, {
				withCredentials: true,
			});
			// Refetch devices after deletion
			queryClient.invalidateQueries({ queryKey: ['devices'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		}
	};

	const showDeleteConfirm = (deviceId: number) => {
		Modal.confirm({
			title: 'Are you sure you want to delete this device?',
			content: 'This action cannot be undone.',
			okText: 'Yes, Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				return handleDeleteDevice(deviceId);
			},
		});
	};

	const handleEditComment = (device: Device) => {
		setCurrentDeviceId(device.id);
		setCommentValue(device.comments || '');
		setCommentModalVisible(true);
	};

	const handleSaveComment = async () => {
		if (!currentDeviceId) return;

		setCommentLoading(true);
		try {
			await axios.patch(
				`/admin/devices/edit/${currentDeviceId}`,
				{
					comments: commentValue,
				},
				{
					withCredentials: true,
				},
			);
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			setCommentModalVisible(false);
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		} finally {
			setCommentLoading(false);
		}
	};

	const handleEditSoldBy = (device: Device) => {
		setCurrentDeviceId(device.id);
		setVendorIdValue(device.sold_by?.vendor_id || '');
		setSoldByModalVisible(true);
	};

	const handleSaveSoldBy = async () => {
		if (!currentDeviceId) return;

		setSoldByLoading(true);
		try {
			await axios.patch(
				`/admin/devices/edit/${currentDeviceId}`,
				{
					sold_by: vendorIdValue,
				},
				{
					withCredentials: true,
				},
			);
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			setSoldByModalVisible(false);
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		} finally {
			setSoldByLoading(false);
		}
	};

	const handleSwapDevice = (device: Device) => {
		setSwapOldDeviceId(device.id);
		setSwapNewDeviceId(device.replaced_by);
		setSwapDeviceModalVisible(true);
	};

	const handleSwapDeviceSuccess = () => {
		queryClient.invalidateQueries({ queryKey: ['devices'] });
		setSwapDeviceModalVisible(false);
	};

	const handleLockDevice = async (deviceId: number) => {
		try {
			await axios.post(
				`/admin/devices/${deviceId}/action`,
				{
					action: 'lock',
				},
				{
					withCredentials: true,
				},
			);
			queryClient.invalidateQueries({ queryKey: ['devices'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		}
	};

	const handleUnlockDevice = async (deviceId: number) => {
		try {
			await axios.post(
				`/admin/devices/${deviceId}/action`,
				{
					action: 'unlock',
				},
				{
					withCredentials: true,
				},
			);
			queryClient.invalidateQueries({ queryKey: ['devices'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		}
	};

	const showLockConfirm = (deviceId: number) => {
		Modal.confirm({
			title: 'Are you sure you want to lock this device?',
			content: 'This will prevent the user from accessing their device.',
			okText: 'Yes, Lock Device',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk() {
				return handleLockDevice(deviceId);
			},
		});
	};

	const showUnlockConfirm = (deviceId: number) => {
		Modal.confirm({
			title: 'Are you sure you want to unlock this device?',
			content: 'This will allow the user to access their device again.',
			okText: 'Yes, Unlock Device',
			okType: 'primary',
			cancelText: 'Cancel',
			onOk() {
				return handleUnlockDevice(deviceId);
			},
		});
	};

	const handleMarkAsRecovered = async (deviceId: number) => {
		try {
			await axios.patch(
				`/admin/devices/edit/${deviceId}`,
				{
					is_recovered: true,
				},
				{
					withCredentials: true,
				},
			);
			queryClient.invalidateQueries({ queryKey: ['devices'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		}
	};

	const handleMarkAsUnrecovered = async (deviceId: number) => {
		try {
			await axios.patch(
				`/admin/devices/edit/${deviceId}`,
				{
					is_recovered: false,
				},
				{
					withCredentials: true,
				},
			);
			queryClient.invalidateQueries({ queryKey: ['devices'] });
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		}
	};

	const submitWriteOff = async () => {
		if (!writeOffDevice || !writeOffReason.trim()) return;
		try {
			setWriteOffBusy(true);
			const res = await axios.post(
				`/admin/super/devices/${writeOffDevice.id}/write-off`,
				{ reason: writeOffReason.trim() },
				{ withCredentials: true },
			);
			setWriteOffDevice(null);
			setWriteOffReason('');
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			message.success(
				`Written off: UGX ${formatNumber(Number(res.data.results.written_off_amount))}`,
			);
		} catch (err) {
			handleAPIError(err as Error);
		} finally {
			setWriteOffBusy(false);
		}
	};

	const showReverseWriteOffConfirm = (deviceId: number) => {
		Modal.confirm({
			title: 'Put this contract back in collection?',
			content: 'Its balance counts as arrears again and penalties resume from today.',
			okText: 'Reverse write-off',
			cancelText: 'Cancel',
			async onOk() {
				try {
					await axios.post(
						`/admin/super/devices/${deviceId}/write-off/reverse`,
						{},
						{ withCredentials: true },
					);
					queryClient.invalidateQueries({ queryKey: ['devices'] });
					message.success('Back in collection');
				} catch (err) {
					handleAPIError(err as Error);
				}
			},
		});
	};

	const showMarkAsRecoveredConfirm = (deviceId: number) => {
		Modal.confirm({
			title: 'Mark this device as recovered?',
			content: 'This will indicate that the device has been recovered.',
			okText: 'Yes, Mark as Recovered',
			okType: 'primary',
			cancelText: 'Cancel',
			onOk() {
				return handleMarkAsRecovered(deviceId);
			},
		});
	};

	const showMarkAsUnrecoveredConfirm = (deviceId: number) => {
		Modal.confirm({
			title: 'Mark this device as unrecovered?',
			content: 'This will indicate that the device has not been recovered.',
			okText: 'Yes, Mark as Unrecovered',
			okType: 'default',
			cancelText: 'Cancel',
			onOk() {
				return handleMarkAsUnrecovered(deviceId);
			},
		});
	};

	const columns: ColumnsType<Device> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'User',
			dataIndex: 'user',
			key: 'user',
			render: (user: UserMinimal) => (
				<div className="flex items-center gap-2">
					<Avatar src={user.dp} />
					<div>
						<span className="text-sm font-medium uppercase">
							{user.first_name} {user.last_name}
						</span>
						<CopyableText text={user.phone_number}>
							<a
								className="text-sm text-gray-500"
								href={`tel:+${user.phone_number}`}
							>
								{user.phone_number}
							</a>
						</CopyableText>
					</div>
				</div>
			),
		},
		{
			title: 'Model',
			dataIndex: 'phone_model',
			key: 'phone_model',
		},
		{
			title: 'Retailer',
			dataIndex: 'retailer',
			key: 'retailer',
			render: (retailer: RetailerMinimal) => (
				<div>
					<span className="text-sm font-medium uppercase">{retailer.business_name}</span>
					<CopyableText
						text={retailer.vendor_id}
						className="text-sm text-gray-500"
					/>
				</div>
			),
		},
		{
			title: 'Sold By',
			dataIndex: 'sold_by',
			key: 'sold_by',
			render: (sold_by: RetailerMinimal | null, device: Device) => (
				<div>
					{sold_by ? (
						<div className="flex items-center gap-2">
							<div>
								<span className="text-sm font-medium uppercase">{sold_by.business_name}</span>
								<CopyableText
									text={sold_by.vendor_id}
									className="text-sm text-gray-500"
								/>
							</div>
							<Button
								type="text"
								size="small"
								icon={<EditOutlined style={{ fontSize: '12px' }} />}
								onClick={() => handleEditSoldBy(device)}
							/>
						</div>
					) : (
						<Button onClick={() => handleEditSoldBy(device)}>Add</Button>
					)}
				</div>
			),
		},
		{
			title: 'Amount',
			dataIndex: 'phone_amount',
			key: 'phone_amount',
			render: (phone_amount: string) => `${formatNumber(phone_amount)} UGX`,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string, device: Device) => (
				<div className="flex flex-col items-center gap-2">
					<div className="flex items-center gap-1">
						{device.is_completed && (
							<Tooltip title="All Installments Paid">
								<CheckCircleFilled className="text-lg text-green-500" />
							</Tooltip>
						)}
					</div>
					<Tag color={getStatusColor(status as Device['status'])}> {status.toUpperCase()}</Tag>
				</div>
			),
		},
		{
			title: 'P-Plan',
			dataIndex: 'payment_plan',
			key: 'payment_plan',
			render: (payment_plan: string, device: Device) => (
				<div className="flex flex-col gap-2">
					<span className="text-nowrap text-sm capitalize text-gray-500">{dayjs(device.approved_at).format('DD MMM YYYY - LT')}</span>
					<span className="text-sm capitalize text-gray-500">Plan: {payment_plan}</span>
					<span className="text-sm text-gray-500">
						Duration: {device.duration} {device.duration_unit}
					</span>
				</div>
			),
		},
		{
			title: 'P-Progress',
			dataIndex: 'total_amount_to_be_paid',
			key: 'total_amount_to_be_paid',
			render: (total_amount_to_be_paid: string, device: Device) =>
				device.status === 'approved' || device.status === 'pending' ? (
					<div className="flex flex-col gap-2">
						<span className="text-sm font-medium uppercase">Total: {formatNumber(total_amount_to_be_paid)} UGX</span>
						<span className="text-sm text-gray-500">Total Paid: {formatNumber(device.total_amount_paid)} UGX</span>
						<span className="text-sm text-gray-500">
							Remaining: {formatNumber(Math.round(parseFloat(total_amount_to_be_paid) - parseFloat(device.total_amount_paid)))} UGX
						</span>
						<span className="text-sm text-gray-500">
							<Badge
								color="green"
								count={device.number_of_installments_paid}
								showZero
								size="small"
							/>{' '}
							of {device.number_of_installments} Installments Paid
						</span>
					</div>
				) : (
					<span>N/A</span>
				),
		},
		{
			title: 'ODP',
			dataIndex: 'has_overdue_payment',
			key: 'has_overdue_payment',
			render: (has_overdue_payment: boolean, device: Device) => (
				<div className="flex flex-col items-center gap-1">
					<div className="flex items-center gap-2">
						<Tag color={has_overdue_payment ? 'red' : 'green'}>{has_overdue_payment ? 'Yes' : 'No'}</Tag>
						{device.is_recovered && (
							<Tooltip title="Device Recovered">
								<CheckCircleFilled className="text-lg text-blue-500" />
							</Tooltip>
						)}
					</div>
					{device.penalty_amount && has_overdue_payment && (
						<div className="text-xs text-red-500">Penalty: {formatNumber(Math.round(parseFloat(device.penalty_amount)))} UGX</div>
					)}
				</div>
			),
		},
		{
			title: 'Comments',
			dataIndex: 'comments',
			key: 'comments',
			render: (comments: string | null, device: Device) => (
				<div className="flex items-center gap-2">
					<div className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
						{comments ? `${comments.substring(0, 10)}${comments.length > 30 ? '...' : ''}` : 'N/A'}
					</div>
					<Button
						type="text"
						icon={<EditOutlined />}
						onClick={() => handleEditComment(device)}
					/>
				</div>
			),
		},
		{
			title: 'Actions',
			dataIndex: 'id',
			key: 'id',
			render: (_: number, device: Device) => (
				<Dropdown
					menu={{
						items: [
							{
								key: 'view',
								label: 'View',
								icon: <EyeOutlined />,
								onClick: () => setSelectedDevice(device),
							},
							...(user?.is_superuser
								? [
										{
											key: 'swap',
											label: 'Swap Device',
											icon: <SwapOutlined />,
											onClick: () => handleSwapDevice(device),
										},
										{
											key: 'lock',
											label: 'Lock Device',
											icon: <LockOutlined />,
											onClick: () => showLockConfirm(device.id),
										},
										{
											key: 'unlock',
											label: 'Unlock Device',
											icon: <UnlockOutlined />,
											onClick: () => showUnlockConfirm(device.id),
										},
										{
											key: 'mark-recovered',
											label: device.is_recovered ? 'Mark as Unrecovered' : 'Mark as Recovered',
											icon: device.is_recovered ? <CloseCircleOutlined /> : <CheckCircleFilled />,
											onClick: () =>
												device.is_recovered ? showMarkAsUnrecoveredConfirm(device.id) : showMarkAsRecoveredConfirm(device.id),
										},
										{
											key: 'write-off',
											label: device.written_off_at
												? 'Reverse Write-off'
												: 'Write Off',
											icon: <FileExcelOutlined />,
											disabled: device.is_completed || device.status !== 'approved',
											onClick: () =>
												device.written_off_at
													? showReverseWriteOffConfirm(device.id)
													: setWriteOffDevice(device),
										},
										{
											key: 'delete',
											label: 'Delete',
											icon: <DeleteOutlined />,
											danger: true,
											onClick: () => showDeleteConfirm(device.id),
										},
									]
								: []),
						],
					}}
					trigger={['click']}
				>
					<Button
						type="text"
						icon={<MoreOutlined />}
					/>
				</Dropdown>
			),
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			status: null,
			device_id: null,
			phone_number: null,
			imei: null,
			retailer: null,
			sold_by: null,
			user: null,
			date_range: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const statusOptions = [
		{ value: 'pending', label: 'Pending' },
		{ value: 'approved', label: 'Approved' },
		{ value: 'rejected', label: 'Rejected' },
	];

	return (
		<AppLayout
			title="Devices"
			subtitle="Manage devices and their contracts."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="device_id"
							label="Device ID"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter device ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="phone_number"
							label="Phone Number"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter phone number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="imei"
							label="IMEI"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter IMEI"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="retailer"
							label="Vendor ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Retailer ID"
								type="text"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="sold_by"
							label="Sold By"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Sold By"
								type="text"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="user"
							label="User ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="User ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="status"
							label="Status"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select status"
								allowClear
								options={statusOptions}
							/>
						</Form.Item>

						<Form.Item
							name="date_range"
							label="Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={devices}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>

			{selectedDevice && (
				<DeviceDetails
					device={selectedDevice}
					onClose={() => setSelectedDevice(null)}
				/>
			)}

			<Modal
				title={`Write off contract #${writeOffDevice?.id ?? ''}`}
				open={!!writeOffDevice}
				onCancel={() => {
					setWriteOffDevice(null);
					setWriteOffReason('');
				}}
				okText="Write off"
				okButtonProps={{
					danger: true,
					loading: writeOffBusy,
					disabled: !writeOffReason.trim(),
				}}
				onOk={submitWriteOff}
			>
				<Form layout="vertical">
					<Form.Item label="Reason" required>
						<Input.TextArea
							rows={3}
							maxLength={255}
							showCount
							value={writeOffReason}
							onChange={(e) => setWriteOffReason(e.target.value)}
							placeholder="Why collection has been given up on"
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Edit Comments"
				open={commentModalVisible}
				onCancel={() => setCommentModalVisible(false)}
				onOk={handleSaveComment}
				confirmLoading={commentLoading}
			>
				<Form layout="vertical">
					<Form.Item label="Comments">
						<Input.TextArea
							rows={4}
							value={commentValue}
							onChange={(e) => setCommentValue(e.target.value)}
							placeholder="Enter comments about this device"
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Update Sold By"
				open={soldByModalVisible}
				onCancel={() => setSoldByModalVisible(false)}
				onOk={handleSaveSoldBy}
				confirmLoading={soldByLoading}
			>
				<Form layout="vertical">
					<Form.Item label="Vendor ID">
						<Input
							value={vendorIdValue}
							onChange={(e) => setVendorIdValue(e.target.value)}
							placeholder="Enter vendor ID"
						/>
					</Form.Item>
				</Form>
			</Modal>

			<SwapDeviceModal
				visible={swapDeviceModalVisible}
				onCancel={() => setSwapDeviceModalVisible(false)}
				onSuccess={handleSwapDeviceSuccess}
				oldDeviceId={swapOldDeviceId}
				newDeviceId={swapNewDeviceId}
			/>
		</AppLayout>
	);
};

export default Devices;
